import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';

function AdminRecharge() {
  const [tab, setTab] = useState('providers');
  const [providers, setProviders] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add Provider form
  const [pName, setPName] = useState('');
  const [pUrl, setPUrl] = useState('');
  const [pMemberId, setPMemberId] = useState('');
  const [pPassword, setPPassword] = useState('');

  // Add Mapping form
  const [mCode, setMCode] = useState('JO');
  const [mName, setMName] = useState('Jio');
  const [mProviderId, setMProviderId] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pRes, mRes] = await Promise.all([
        api.get('/AdminRecharge/providers'),
        api.get('/AdminRecharge/mappings'),
      ]);
      setProviders(pRes.data.data || []);
      setMappings(mRes.data.data || []);
      if (pRes.data.data?.length > 0) {
        setMProviderId(pRes.data.data[0].id);
      }
    } catch (err) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async (e) => {
    e.preventDefault();
    try {
      await api.post('/AdminRecharge/providers', {
        name: pName,
        baseUrl: pUrl,
        apiMemberId: pMemberId,
        apiPassword: pPassword,
        priority: 1,
      });
      toast.success('Provider added');
      setPName('');
      setPUrl('');
      setPMemberId('');
      setPPassword('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleToggleProvider = async (id) => {
    try {
      await api.put(`/AdminRecharge/providers/${id}/toggle`);
      toast.success('Updated');
      loadData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleAddMapping = async (e) => {
    e.preventDefault();
    try {
      await api.post('/AdminRecharge/mappings', {
        operatorCode: mCode,
        operatorName: mName,
        providerId: Number(mProviderId),
      });
      toast.success('Mapping added');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleToggleMapping = async (id, currentActive) => {
    try {
      await api.put(`/AdminRecharge/mappings/${id}`, {
        isActive: !currentActive,
      });
      toast.success('Updated');
      loadData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleChangeProvider = async (id, newProviderId) => {
    try {
      await api.put(`/AdminRecharge/mappings/${id}`, {
        providerId: Number(newProviderId),
      });
      toast.success('Provider changed');
      loadData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Admin – Recharge Setup</h1>
            <p className="text-gray-500 text-sm">Providers & Operator Mapping</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="bg-gray-200 px-4 py-2 rounded-lg text-sm"
          >
            ← Back
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab('providers')}
            className={`px-4 py-2 rounded-lg font-medium ${
              tab === 'providers' ? 'bg-blue-600 text-white' : 'bg-white'
            }`}
          >
            Providers
          </button>
          <button
            onClick={() => setTab('mappings')}
            className={`px-4 py-2 rounded-lg font-medium ${
              tab === 'mappings' ? 'bg-blue-600 text-white' : 'bg-white'
            }`}
          >
            Operator Mapping
          </button>
        </div>

        {/* ===== PROVIDERS TAB ===== */}
        {tab === 'providers' && (
          <div className="space-y-4">
            {/* Add Form */}
            <form onSubmit={handleAddProvider} className="bg-white rounded-2xl p-5 shadow space-y-3">
              <h3 className="font-bold">Add Provider</h3>
              <input
                value={pName}
                onChange={(e) => setPName(e.target.value)}
                placeholder="Name (e.g. RoboticExchange)"
                className="w-full border rounded-xl px-4 py-2"
                required
              />
              <input
                value={pUrl}
                onChange={(e) => setPUrl(e.target.value)}
                placeholder="Base URL"
                className="w-full border rounded-xl px-4 py-2"
              />
              <input
                value={pMemberId}
                onChange={(e) => setPMemberId(e.target.value)}
                placeholder="API Member ID"
                className="w-full border rounded-xl px-4 py-2"
              />
              <input
                value={pPassword}
                onChange={(e) => setPPassword(e.target.value)}
                placeholder="API Password"
                type="password"
                className="w-full border rounded-xl px-4 py-2"
              />
              <button type="submit" className="w-full bg-blue-600 text-white font-bold py-2 rounded-xl">
                Add Provider
              </button>
            </form>

            {/* List */}
            {providers.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl p-4 shadow flex justify-between items-center">
                <div>
                  <p className="font-bold">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.baseUrl}</p>
                  <p className="text-xs text-gray-400">ID: {p.apiMemberId}</p>
                </div>
                <button
                  onClick={() => handleToggleProvider(p.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-bold text-white ${
                    p.isActive ? 'bg-green-600' : 'bg-gray-400'
                  }`}
                >
                  {p.isActive ? 'ON' : 'OFF'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ===== MAPPINGS TAB ===== */}
        {tab === 'mappings' && (
          <div className="space-y-4">
            {/* Add Form */}
            <form onSubmit={handleAddMapping} className="bg-white rounded-2xl p-5 shadow space-y-3">
              <h3 className="font-bold">Add Operator Mapping</h3>
              <input
                value={mCode}
                onChange={(e) => setMCode(e.target.value.toUpperCase())}
                placeholder="Code (JO, AT, VI, BS)"
                className="w-full border rounded-xl px-4 py-2"
                required
              />
              <input
                value={mName}
                onChange={(e) => setMName(e.target.value)}
                placeholder="Name (Jio, Airtel...)"
                className="w-full border rounded-xl px-4 py-2"
                required
              />
              <select
                value={mProviderId}
                onChange={(e) => setMProviderId(e.target.value)}
                className="w-full border rounded-xl px-4 py-2"
              >
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button type="submit" className="w-full bg-blue-600 text-white font-bold py-2 rounded-xl">
                Add Mapping
              </button>
            </form>

            {/* List */}
            {mappings.map((m) => (
              <div key={m.id} className="bg-white rounded-2xl p-4 shadow">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <p className="font-bold">
                      {m.operatorName} ({m.operatorCode})
                    </p>
                    <p className="text-sm text-gray-500">→ {m.providerName}</p>
                  </div>
                  <button
                    onClick={() => handleToggleMapping(m.id, m.isActive)}
                    className={`px-3 py-1 rounded-lg text-sm font-bold text-white ${
                      m.isActive ? 'bg-green-600' : 'bg-gray-400'
                    }`}
                  >
                    {m.isActive ? 'ON' : 'OFF'}
                  </button>
                </div>
                <select
                  value={m.providerId}
                  onChange={(e) => handleChangeProvider(m.id, e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminRecharge;